import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Info as InfoIcon,
  Refresh as RefreshIcon,
  List as ListIcon
} from '@mui/icons-material';
import { api } from '../services/authService';

const LeadListSelectionInterface = ({ 
  accountId, 
  campaignId, 
  nurturingCampaignId,
  onLeadListsChange,
  initialSelectedLists = []
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableLists, setAvailableLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState(initialSelectedLists);
  const [totalLeadsFromLists, setTotalLeadsFromLists] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);

  // Fetch available lead lists
  useEffect(() => {
    const fetchAvailableLists = async () => {
      if (!accountId || !campaignId) return;
      
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.append('account', accountId);
        params.append('campaign', campaignId);
        
        const response = await api.get(`/crm/lists/?${params.toString()}`);
        setAvailableLists(response.data || []);
      } catch (err) {
        setError('Failed to load available lead lists');
        console.error('Error loading lead lists:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableLists();
  }, [accountId, campaignId]);

  // Update total leads count when selected lists change
  useEffect(() => {
    const updateTotalLeads = async () => {
      if (selectedLists.length === 0) {
        setTotalLeadsFromLists(0);
        onLeadListsChange([], 0);
        return;
      }

      setLoadingCount(true);
      try {
        // Calculate total leads from selected lists
        const totalLeads = selectedLists.reduce((sum, list) => sum + (list.lead_count || 0), 0);
        setTotalLeadsFromLists(totalLeads);
        onLeadListsChange(selectedLists, totalLeads);
      } catch (err) {
        console.error('Error calculating total leads:', err);
      } finally {
        setLoadingCount(false);
      }
    };

    updateTotalLeads();
  }, [selectedLists, onLeadListsChange]);

  const handleListToggle = (list) => {
    setSelectedLists(prev => {
      const isSelected = prev.some(selectedList => selectedList.id === list.id);
      if (isSelected) {
        return prev.filter(selectedList => selectedList.id !== list.id);
      } else {
        return [...prev, list];
      }
    });
  };

  const handleRemoveList = (listId) => {
    setSelectedLists(prev => prev.filter(list => list.id !== listId));
  };

  const handleRefreshLists = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('account', accountId);
      params.append('campaign', campaignId);
      
      const response = await api.get(`/crm/lists/?${params.toString()}`);
      setAvailableLists(response.data || []);
    } catch (err) {
      setError('Failed to refresh lead lists');
      console.error('Error refreshing lead lists:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'manual': return 'Manual';
      case 'dynamic': return 'Dynamic';
      case 'filtered': return 'Filtered';
      default: return type;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Lead List Selection
        </Typography>
        <Tooltip title="Select from available lead lists to assign to this campaign">
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh available lists">
          <IconButton 
            onClick={handleRefreshLists} 
            disabled={loading}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Available Lead Lists
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select one or more lead lists to assign to this nurturing campaign. These lists are eligible for assignment because they belong to the same CRM campaign or account.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : availableLists.length === 0 ? (
          <Alert severity="info">
            No lead lists are available for this campaign. Create lead lists in the CRM first.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {availableLists.map((list) => (
              <Grid item xs={12} key={list.id}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    cursor: 'pointer',
                    borderColor: selectedLists.some(selected => selected.id === list.id) ? 'primary.main' : 'divider',
                    backgroundColor: selectedLists.some(selected => selected.id === list.id) ? 'primary.50' : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50'
                    }
                  }}
                  onClick={() => handleListToggle(list)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Checkbox
                        checked={selectedLists.some(selected => selected.id === list.id)}
                        onChange={() => handleListToggle(list)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Box sx={{ ml: 1, flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {list.name}
                        </Typography>
                        {list.description && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                            {list.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip 
                            label={getTypeLabel(list.list_type)} 
                            size="small" 
                            variant="outlined"
                            icon={<ListIcon />}
                          />
                          <Chip 
                            label={list.status} 
                            color={getStatusColor(list.status)}
                            size="small"
                          />
                                                     <Chip 
                             label={`${list.lead_count || 0} leads`}
                             size="small"
                             variant="outlined"
                           />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {selectedLists.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected Lead Lists
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {selectedLists.map((list) => (
              <Chip
                key={list.id}
                                 label={`${list.name} (${list.lead_count || 0} leads)`}
                onDelete={() => handleRemoveList(list.id)}
                color="primary"
                variant="outlined"
                icon={<ListIcon />}
              />
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              Total leads from selected lists:
            </Typography>
            <Typography variant="h6" color="primary">
              {loadingCount ? <CircularProgress size={20} /> : totalLeadsFromLists}
            </Typography>
          </Box>
        </Paper>
      )}

      {availableLists.length > 0 && selectedLists.length === 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Getting Started with Lead List Selection
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Click on any lead list above to select it for assignment to this nurturing campaign. You can select multiple lists to combine their leads.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label="Multiple lists supported" size="small" variant="outlined" />
            <Chip label="Automatic deduplication" size="small" variant="outlined" />
            <Chip label="Real-time lead count" size="small" variant="outlined" />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default LeadListSelectionInterface; 